import { useState, useEffect } from 'react';
import { fetchFacility, submitContact } from '@/lib/api';
import { MapPin, Phone, Mail, Send, Clock, Navigation } from 'lucide-react';
import SEO from '@/components/SEO';

const MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3236.5!2d-78.8503!3d35.7327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac8f4b88e0e1a7%3A0x0!2s810+Center+St%2C+Apex%2C+NC+27502!5e0!3m2!1sen!2sus!4v1700000000000";
const MAPS_LINK = "https://www.google.com/maps/dir/?api=1&destination=810+Center+St+Apex+NC+27502";
const PHONE = '(919) 267-0943';
const ADDRESS = '810 Center St, Building #4, Apex NC, 27502';
const EMAIL = 'info@propelsportsnc.com';
const INPUT_CLS = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-[#0B1D3A] text-sm placeholder-gray-400 focus:outline-none focus:border-[#F26522] focus:ring-1 focus:ring-[#F26522]/30 transition-all";

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
        <p className="text-green-700 font-semibold text-lg mb-2">Message Sent!</p>
        <p className="text-gray-500 text-sm">We'll get back to you as soon as possible.</p>
        <button onClick={() => setSent(false)} className="mt-4 text-[#F26522] text-sm font-medium hover:underline">Send Another Message</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-4">
      {err && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-2">{err}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 text-xs mb-1.5 font-medium">Name</label>
          <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            data-testid="contact-name" className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-gray-600 text-xs mb-1.5 font-medium">Email</label>
          <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
            data-testid="contact-email" className={INPUT_CLS} />
        </div>
      </div>
      <div>
        <label className="block text-gray-600 text-xs mb-1.5 font-medium">Subject</label>
        <input type="text" required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
          data-testid="contact-subject" className={INPUT_CLS} />
      </div>
      <div>
        <label className="block text-gray-600 text-xs mb-1.5 font-medium">Message</label>
        <textarea rows={5} required value={form.message} onChange={e => setForm({...form, message: e.target.value})}
          data-testid="contact-message" className={INPUT_CLS + " resize-none"} />
      </div>
      <button type="submit" disabled={loading} data-testid="contact-submit"
        className="w-full flex items-center justify-center gap-2 bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-6 py-3.5 rounded-lg font-semibold transition-all">
        <Send size={16} /> {loading ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

function ContactInfo({ hours }) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#0B1D3A]">Get In Touch</h2>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <MapPin size={18} className="text-[#F26522] mt-0.5" />
          <div className="text-gray-600 text-sm">{ADDRESS}</div>
        </div>
        <div className="flex items-center gap-3">
          <Phone size={18} className="text-[#F26522]" />
          <a href="tel:+19192670943" className="text-gray-600 text-sm hover:text-[#F26522] transition-colors">{PHONE}</a>
        </div>
        <div className="flex items-center gap-3">
          <Mail size={18} className="text-[#F26522]" />
          <a href={`mailto:${EMAIL}`} className="text-gray-600 text-sm hover:text-[#F26522] transition-colors">{EMAIL}</a>
        </div>
        {Object.keys(hours).length > 0 && (
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-[#F26522] mt-0.5" />
            <div>
              <div className="text-[#0B1D3A] text-sm font-medium mb-1">Hours</div>
              <div className="text-gray-500 text-xs space-y-0.5">
                {Object.entries(hours).slice(0, 3).map(([day, time]) => (
                  <div key={day} className="capitalize">{day}: {time}</div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" data-testid="get-directions-btn"
        className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all mt-2">
        <Navigation size={15} /> Get Directions
      </a>
    </div>
  );
}

export default function Contact() {
  const [facility, setFacility] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFacility().then(setFacility).catch(() => {}); }, []);

  const hours = facility?.hours || {};

  return (
    <div data-testid="contact-page" className="pt-[68px]">
      <SEO title="Contact Us" description="Get in touch with Propel Sports NC. Visit us at 810 Center St, Building #4, Apex NC, 27502 or call (919) 267-0943." path="/contact" />

      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 50% 50%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-gray-300 text-lg">We'd love to hear from you. Get in touch today!</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <ContactInfo hours={hours} />
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-6 text-center">Find Us</h2>
          <div data-testid="google-maps-embed" className="rounded-2xl overflow-hidden shadow-md border border-gray-200">
            <iframe src={MAPS_EMBED} width="100%" height="400" style={{ border: 0 }} allowFullScreen loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" title="Propel Sports NC - 810 Center St, Building #4, Apex NC, 27502" />
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">{ADDRESS}</p>
        </div>
      </section>
    </div>
  );
}
